import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, Trash2, User, LogOut, LayoutDashboard, Box, FolderOpen, ClipboardList, Upload, FileSpreadsheet, Calculator, MapPin, Clock, Check, AlertCircle, Gift, ChevronDown, HelpCircle, Edit, Save, Image, Settings, Ruler, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { AdminLayout } from "./AdminCore";

// Admin Products Page
const AdminProducts = () => {
  const { authAxios } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [csvFile, setCsvFile] = useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    category_id: "",
    category_ids: [],
    cost_price: "",
    tax_rate: "7.5",
    margin_rate: "20",
    stock: "",
    images: "",
    weight_kg: "0.5",
    has_sizes: false,
    size_type: "",
    sizes: []
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      authAxios.get("/products?active_only=false"),
      axios.get(`${API}/categories`)
    ])
      .then(([productsRes, categoriesRes]) => {
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculatePricing = () => {
    const cost = parseFloat(form.cost_price) || 0;
    const taxRate = parseFloat(form.tax_rate) || 0;
    const marginRate = parseFloat(form.margin_rate) || 0;
    const taxAmount = cost * (taxRate / 100);
    const marginAmount = cost * (marginRate / 100);
    const sellingPrice = cost + taxAmount + marginAmount;
    return { taxAmount, marginAmount, sellingPrice };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Combine uploaded images with any URL-based images
      const allImages = [
        ...uploadedImages,
        ...(form.images ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : [])
      ];
      
      const data = {
        ...form,
        cost_price: parseFloat(form.cost_price),
        tax_rate: parseFloat(form.tax_rate),
        margin_rate: parseFloat(form.margin_rate),
        stock: form.has_sizes ? form.sizes.reduce((sum, s) => sum + (parseInt(s.stock) || 0), 0) : parseInt(form.stock),
        weight_kg: parseFloat(form.weight_kg),
        images: allImages,
        category_id: form.category_id,
        category_ids: form.category_ids.length > 0 ? form.category_ids : [form.category_id],
        has_sizes: form.has_sizes,
        size_type: form.has_sizes ? form.size_type : null,
        sizes: form.has_sizes ? form.sizes.map(s => ({ ...s, stock: parseInt(s.stock) || 0 })) : null
      };

      if (editProduct) {
        await authAxios.put(`/products/${editProduct.id}`, data);
        toast.success("Product updated!");
      } else {
        await authAxios.post("/products", data);
        toast.success("Product created!");
      }
      setShowForm(false);
      setEditProduct(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving product");
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    const formData = new FormData();
    formData.append("file", csvFile);
    try {
      const res = await authAxios.post("/products/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Imported ${res.data.imported} products`);
      if (res.data.errors.length > 0) {
        console.log("Import errors:", res.data.errors);
      }
      setCsvFile(null);
      fetchData();
    } catch (err) {
      toast.error("Import failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await authAxios.delete(`/products/${id}`);
      toast.success("Product deleted");
      fetchData();
    } catch (err) {
      toast.error("Error deleting product");
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      category_id: "",
      category_ids: [],
      cost_price: "",
      tax_rate: "7.5",
      margin_rate: "20",
      stock: "",
      images: "",
      weight_kg: "0.5",
      has_sizes: false,
      size_type: "",
      sizes: []
    });
    setUploadedImages([]);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      
      const res = await authAxios.post("/upload/images", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const newUrls = res.data.uploaded.map((img) => img.url);
      setUploadedImages((prev) => [...prev, ...newUrls]);
      toast.success(`${res.data.count} image(s) uploaded!`);
    } catch (err) {
      toast.error("Failed to upload images");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeUploadedImage = (idx) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setForm({
      name: product.name,
      description: product.description,
      category_id: product.category_id,
      category_ids: product.category_ids || [product.category_id],
      cost_price: product.cost_price.toString(),
      tax_rate: product.tax_rate.toString(),
      margin_rate: product.margin_rate.toString(),
      stock: product.stock.toString(),
      images: "",
      weight_kg: product.weight_kg.toString(),
      has_sizes: product.has_sizes || false,
      size_type: product.size_type || "",
      sizes: product.sizes || []
    });
    setUploadedImages(product.images || []);
    setShowForm(true);
  };

  const { taxAmount, marginAmount, sellingPrice } = calculatePricing();
  const formatPrice = (price) => `₦${price.toLocaleString()}`;

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <h1 className="text-3xl font-black uppercase tracking-tight" data-testid="admin-products-title">Products</h1>
        <div className="flex gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="btn-outline flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" /> Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Import Products from CSV</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  CSV should have columns: name, description, category, cost_price, tax_rate, margin_rate, stock, images (pipe-separated), weight_kg
                </p>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                />
                <Button onClick={handleCsvImport} className="btn-primary w-full" disabled={!csvFile}>
                  Import
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={() => { resetForm(); setEditProduct(null); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-product-button">
            <Plus className="w-4 h-4" /> Add Product
          </Button>
        </div>
      </div>

      {/* Product Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase">{editProduct ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="font-bold">Product Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label className="font-bold">Primary Category</Label>
                <Select value={form.category_id} onValueChange={(v) => {
                  const newIds = form.category_ids.includes(v) ? form.category_ids : [...form.category_ids, v];
                  setForm({ ...form, category_id: v, category_ids: newIds });
                }} required>
                  <SelectTrigger className="mt-1" data-testid="product-category-select">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Multi-Category Selection */}
            {categories.length > 1 && (
              <div>
                <Label className="font-bold text-sm">Additional Categories <span className="text-gray-400 font-normal">(product will appear in all selected)</span></Label>
                <div className="mt-2 border rounded-md p-3 max-h-36 overflow-y-auto space-y-1 bg-white">
                  {categories.map((cat) => (
                    <label key={cat.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#F5F5F5] px-2 py-1 rounded text-sm">
                      <input
                        type="checkbox"
                        checked={form.category_ids.includes(cat.id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...form.category_ids, cat.id]
                            : form.category_ids.filter((id) => id !== cat.id && id !== form.category_id);
                          setForm({ ...form, category_ids: ids.length > 0 ? ids : [form.category_id] });
                        }}
                        className="accent-[#FF6B00]"
                        data-testid={`category-checkbox-${cat.id}`}
                      />
                      <span>{cat.name}</span>
                    </label>
                  ))}
                </div>
                {form.category_ids.length > 1 && (
                  <p className="text-xs text-[#FF6B00] mt-1 font-bold">Product in {form.category_ids.length} categories</p>
                )}
              </div>
            )}

            <div>
              <Label className="font-bold">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Markup Calculator */}
            <div className="bg-[#F5F5F5] border-2 border-[#0A0A0A] p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-5 h-5" />
                <h3 className="font-black uppercase">Markup Calculator</h3>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <Label className="font-bold">Cost Price (₦)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.cost_price}
                    onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="product-cost-input"
                  />
                </div>
                <div>
                  <Label className="font-bold">Tax Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.tax_rate}
                    onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="font-bold">Margin Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.margin_rate}
                    onChange={(e) => setForm({ ...form, margin_rate: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white p-4 border border-[#E5E5E5]">
                  <p className="text-xs text-gray-500 uppercase">Tax Amount</p>
                  <p className="text-xl font-bold">{formatPrice(taxAmount.toFixed(2))}</p>
                </div>
                <div className="bg-white p-4 border border-[#E5E5E5]">
                  <p className="text-xs text-gray-500 uppercase">Your Profit</p>
                  <p className="text-xl font-bold text-green-600">{formatPrice(marginAmount.toFixed(2))}</p>
                </div>
                <div className="bg-[#0A0A0A] text-white p-4">
                  <p className="text-xs text-gray-400 uppercase">Selling Price</p>
                  <p className="text-xl font-bold text-[#FF6B00]">{formatPrice(sellingPrice.toFixed(2))}</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="font-bold">Stock Quantity {form.has_sizes && <span className="text-xs text-gray-400">(auto-calculated from sizes)</span>}</Label>
                <Input
                  type="number"
                  value={form.has_sizes ? form.sizes.reduce((sum, s) => sum + (parseInt(s.stock) || 0), 0) : form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  required={!form.has_sizes}
                  disabled={form.has_sizes}
                  className="mt-1"
                  data-testid="product-stock-input"
                />
              </div>
              <div>
                <Label className="font-bold">Weight (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.weight_kg}
                  onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            {/* Size Management Section */}
            <div className="bg-[#F5F5F5] border-2 border-[#0A0A0A] p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Ruler className="w-5 h-5" />
                  <h3 className="font-black uppercase">Size Management</h3>
                </div>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="has-sizes-toggle">
                  <span className="text-sm font-bold">{form.has_sizes ? "Enabled" : "Disabled"}</span>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, has_sizes: !form.has_sizes, sizes: !form.has_sizes ? (form.sizes.length > 0 ? form.sizes : [{ size: "", stock: 0 }]) : form.sizes })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${form.has_sizes ? "bg-[#FF6B00]" : "bg-gray-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.has_sizes ? "translate-x-6" : ""}`} />
                  </button>
                </label>
              </div>

              {form.has_sizes && (
                <div className="space-y-4">
                  <div>
                    <Label className="font-bold text-sm">Size Type</Label>
                    <Select value={form.size_type} onValueChange={(v) => setForm({ ...form, size_type: v })} data-testid="size-type-select">
                      <SelectTrigger className="mt-1 bg-white" data-testid="size-type-trigger">
                        <SelectValue placeholder="Select size type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clothing">Clothing (S, M, L, XL...)</SelectItem>
                        <SelectItem value="footwear">Footwear (UK/EU/US)</SelectItem>
                        <SelectItem value="rings">Rings</SelectItem>
                        <SelectItem value="chains">Chains / Necklaces</SelectItem>
                        <SelectItem value="bracelets">Bracelets / Bangles</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-bold text-sm">Sizes & Stock</Label>
                      <Button
                        type="button"
                        onClick={() => setForm({ ...form, sizes: [...form.sizes, { size: "", stock: 0 }] })}
                        className="h-8 px-3 text-xs bg-[#0A0A0A] text-white hover:bg-[#333]"
                        data-testid="add-size-button"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Size
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {form.sizes.map((sizeItem, idx) => (
                        <div key={idx} className="flex gap-2 items-center" data-testid={`size-row-${idx}`}>
                          <Input
                            value={sizeItem.size}
                            onChange={(e) => {
                              const updated = [...form.sizes];
                              updated[idx] = { ...updated[idx], size: e.target.value };
                              setForm({ ...form, sizes: updated });
                            }}
                            placeholder="Size (e.g. M, 42, 7)"
                            className="flex-1 bg-white"
                            data-testid={`size-name-input-${idx}`}
                          />
                          <Input
                            type="number"
                            value={sizeItem.stock}
                            onChange={(e) => {
                              const updated = [...form.sizes];
                              updated[idx] = { ...updated[idx], stock: parseInt(e.target.value) || 0 };
                              setForm({ ...form, sizes: updated });
                            }}
                            placeholder="Stock"
                            className="w-24 bg-white"
                            min="0"
                            data-testid={`size-stock-input-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, sizes: form.sizes.filter((_, i) => i !== idx) })}
                            className="p-2 text-red-500 hover:bg-red-50"
                            data-testid={`remove-size-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    {form.sizes.length > 0 && (
                      <p className="text-xs text-gray-500 mt-2">
                        Total stock across all sizes: <span className="font-bold text-[#FF6B00]">{form.sizes.reduce((sum, s) => sum + (parseInt(s.stock) || 0), 0)}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="font-bold">Product Images</Label>
              <div className="mt-2 space-y-4">
                {/* Upload from device */}
                <div className="border-2 border-dashed border-[#E5E5E5] p-6 text-center hover:border-[#FF6B00] transition-colors">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    multiple
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                    <p className="font-bold">Click to upload images</p>
                    <p className="text-sm text-gray-500">Upload from phone, tablet, or computer</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB each</p>
                  </label>
                </div>
                
                {uploading && (
                  <div className="flex items-center justify-center gap-2 text-[#FF6B00]">
                    <div className="spinner w-4 h-4" />
                    <span>Uploading...</span>
                  </div>
                )}
                
                {/* Uploaded images preview */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {uploadedImages.map((url, idx) => (
                      <div key={url} className="relative group">
                        <img src={url} alt={`Product ${idx + 1}`} className="w-full aspect-square object-cover border-2 border-[#E5E5E5]" />
                        <button
                          type="button"
                          onClick={() => removeUploadedImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 bg-[#FF6B00] text-[#0A0A0A] text-xs px-2 py-0.5 font-bold">Main</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* URL input as fallback */}
                <div>
                  <Label className="text-sm text-gray-500">Or add image URLs (comma-separated)</Label>
                  <Input
                    value={form.images}
                    onChange={(e) => setForm({ ...form, images: e.target.value })}
                    placeholder="https://example.com/img1.jpg, https://example.com/img2.jpg"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-product-button">
                {editProduct ? "Update Product" : "Create Product"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Products Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-24 bg-white border-2 border-[#0A0A0A]">
          <Box className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No products yet</h3>
          <p className="text-gray-500 mb-6">Add your first product to get started</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-[#0A0A0A] overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Cost</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} data-testid={`product-row-${product.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img src={product.images?.[0] || "/placeholder.jpg"} alt="" className="w-12 h-12 object-cover" />
                      <span className="font-bold">{product.name}</span>
                    </div>
                  </td>
                  <td>{product.category_name}</td>
                  <td>{formatPrice(product.cost_price)}</td>
                  <td className="font-bold text-[#FF6B00]">{formatPrice(product.selling_price)}</td>
                  <td className={product.stock < 10 ? "text-red-600 font-bold" : ""}>{product.stock}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-bold ${product.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                      {product.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(product)} className="text-blue-600 hover:text-blue-800">Edit</button>
                      <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-800">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};


export { AdminProducts };
